"use client";

import { m as motion, useInView, useReducedMotion } from "framer-motion";
import { useEffect, useRef, useState } from "react";

interface FaqVisualProps {
  isItalian: boolean;
  /** Parole da ciclare in basso; se assente usa i temi FAQ di default (MVP). */
  words?: string[];
}

/**
 * Companion animato per la sezione FAQ dell'MVP.
 * Tema: dal dubbio alla chiarezza. Due bolle della STESSA dimensione disposte
 * in DIAGONALE: la bolla-domanda con un "?" pulsante in alto a sinistra, i tre
 * puntini "sta scrivendo" al centro (in diagonale), la bolla-risposta con un
 * check in basso a destra. La conversazione "scorre" in diagonale.
 * Sotto, le parole-tema delle FAQ che ciclano una alla volta (maiuscolo, bianco).
 * Card larga (riempie la colonna) e a tutta altezza dell'accordion.
 * Line-art ciano, solo transform/opacity, loop dolce.
 * Hover premium: il bordo diventa bianco (solo colore, nessun layout shift).
 * Rispetta useReducedMotion (statico) ed e' gated con useInView (stop off-screen).
 */

const S = "hsl(var(--primary))";
const EASE = [0.16, 1, 0.3, 1] as const;

export function FaqVisual({ isItalian, words: wordsProp }: FaqVisualProps) {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { margin: "-80px" });
  const loop = inView && !reduce;

  // Parole che ciclano: i temi delle FAQ (una visibile alla volta).
  const words =
    wordsProp ??
    (isItalian
      ? ["Prezzo", "Tempi", "Codice", "Supporto"]
      : ["Pricing", "Timing", "Code", "Support"]);
  const [wordIndex, setWordIndex] = useState(0);

  useEffect(() => {
    if (!loop) return;
    const id = window.setInterval(() => {
      setWordIndex((i) => (i + 1) % words.length);
    }, 2000);
    return () => window.clearInterval(id);
  }, [loop, words.length]);

  return (
    // Card larga: riempie la colonna (w-full, niente max-w) e va a tutta altezza
    // della riga accordion (h-full). Piccolo pr per non toccare la label/nav a
    // destra pur restando dentro il container. group = hover premium sul bordo.
    <div ref={ref} className="group relative h-full w-full pr-2">
      {/* Glow di sfondo che respira piano */}
      <motion.div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 rounded-full bg-primary/10 blur-3xl"
        initial={false}
        animate={loop ? { opacity: [0.55, 1, 0.55] } : { opacity: 0.7 }}
        transition={
          loop
            ? { duration: 5, ease: "easeInOut", repeat: Infinity }
            : { duration: 0.4 }
        }
      />

      {/* Hover premium: il bordo passa a bianco (solo colore, transizione dolce,
          nessun layout shift). h-full per allinearsi in altezza all'accordion. */}
      <div className="relative flex h-full w-full flex-col overflow-hidden rounded-3xl border border-[hsl(var(--border))] bg-surface/40 transition-colors duration-300 group-hover:border-foreground">
        <div
          className="pointer-events-none absolute inset-0 pattern-dots opacity-40"
          aria-hidden="true"
        />

        {/* Scena animata: due bolle uguali in DIAGONALE (domanda in alto sx,
            risposta in basso dx) con i puntini lungo la diagonale centrale. */}
        <div className="relative flex flex-1 items-center justify-center px-8">
          <svg
            viewBox="0 0 100 120"
            className="h-full w-full max-h-[18rem]"
            preserveAspectRatio="xMidYMid meet"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id="faq-bubble-fill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={S} stopOpacity={0.14} />
                <stop offset="100%" stopColor={S} stopOpacity={0.04} />
              </linearGradient>
            </defs>

            {/* Bolla-domanda in ALTO A SINISTRA: il "?" pulsa (il dubbio).
                Bolla 44x26 (coda in basso), stessa dimensione della risposta. */}
            <motion.g
              initial={reduce ? false : { opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: EASE }}
            >
              <path
                d="M12 8
                   H46 A8 8 0 0 1 54 16
                   V26 A8 8 0 0 1 46 34
                   H26 L19 41 L20.5 34
                   H12 A8 8 0 0 1 4 26
                   V16 A8 8 0 0 1 12 8 Z"
                fill="url(#faq-bubble-fill)"
                stroke={S}
                strokeWidth={1.6}
                strokeOpacity={0.7}
                strokeLinejoin="round"
              />
              <motion.text
                x={29}
                y={25}
                textAnchor="middle"
                fontSize={16}
                fontWeight={700}
                fill={S}
                style={{ transformBox: "fill-box", transformOrigin: "center" }}
                initial={false}
                animate={loop ? { scale: [1, 1.18, 1] } : { scale: 1 }}
                transition={
                  loop
                    ? { duration: 2.4, ease: "easeInOut", repeat: Infinity }
                    : { duration: 0.3 }
                }
              >
                ?
              </motion.text>
            </motion.g>

            {/* Puntini "sta scrivendo" DIAGONALI: dalla bolla-domanda (alto sx)
                verso la bolla-risposta (basso dx), lungo la diagonale centrale. */}
            <g>
              {[
                { cx: 36, cy: 46 },
                { cx: 50, cy: 60 },
                { cx: 64, cy: 74 },
              ].map(({ cx, cy }, i) => (
                <motion.circle
                  key={cx}
                  cx={cx}
                  cy={cy}
                  r={2.6}
                  fill={S}
                  style={{ transformBox: "fill-box", transformOrigin: "center" }}
                  initial={false}
                  animate={
                    loop
                      ? { opacity: [0.25, 1, 0.25], scale: [0.8, 1.15, 0.8] }
                      : { opacity: 0.6, scale: 1 }
                  }
                  transition={
                    loop
                      ? {
                          duration: 1.5,
                          ease: "easeInOut",
                          repeat: Infinity,
                          delay: i * 0.2,
                        }
                      : { duration: 0.3 }
                  }
                />
              ))}
            </g>

            {/* Bolla-risposta in BASSO A DESTRA: entra fluttuando (la chiarezza).
                Bolla 44x26 (coda in alto sx verso i puntini), STESSA dimensione
                della bolla-domanda. */}
            <motion.g
              initial={reduce ? false : { opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.35, ease: EASE }}
            >
              <motion.g
                initial={false}
                animate={loop ? { y: [0, -3, 0] } : { y: 0 }}
                transition={
                  loop
                    ? { duration: 4, ease: "easeInOut", repeat: Infinity }
                    : { duration: 0.3 }
                }
              >
                <path
                  d="M49 86
                     H67 L74 79 L72.5 86
                     H83 A8 8 0 0 1 91 94
                     V104 A8 8 0 0 1 83 112
                     H49 A8 8 0 0 1 41 104
                     V94 A8 8 0 0 1 49 86 Z"
                  fill={S}
                  fillOpacity={0.12}
                  stroke={S}
                  strokeWidth={1.6}
                  strokeLinejoin="round"
                />
                {/* Il check che si "disegna": il dubbio risolto. Dimensionato per
                    leggere alla STESSA scala del "?" nella bolla-domanda (stessa
                    altezza visiva ~11u, centrato nella bolla-risposta). */}
                <motion.text
                x={65}
                y={105}
                textAnchor="middle"
                fontSize={16}
                fontWeight={700}
                fill={S}
                style={{ transformBox: "fill-box", transformOrigin: "center" }}
                initial={false}
                animate={loop ? { scale: [1, 1.18, 1] } : { scale: 1 }}
                transition={
                  loop
                    ? { duration: 2.4, ease: "easeInOut", repeat: Infinity }
                    : { duration: 0.3 }
                }
              >
                ✓
              </motion.text>
              </motion.g>
            </motion.g>
          </svg>
        </div>

        {/* Base: le parole-tema delle FAQ che ciclano (una alla volta),
            maiuscolo e piu' grandi per essere il testo protagonista. */}
        <div className="relative flex flex-col items-center px-8 pb-10">
          {/* Parola che cambia una alla volta (fade/slide), bianca, tasteful */}
          <div className="relative h-0 w-full">
            {reduce ? (
              <span className="absolute inset-0 flex items-center justify-center font-display text-xl font-bold uppercase tracking-[0.12em] text-foreground">
                {words[0]}
              </span>
            ) : (
              words.map((word, i) => (
                <motion.span
                  key={word}
                  className="absolute inset-0 flex items-center justify-center font-display text-xl font-bold uppercase tracking-[0.12em] text-foreground"
                  initial={false}
                  animate={
                    i === wordIndex
                      ? { opacity: 1, y: 0 }
                      : { opacity: 0, y: 6 }
                  }
                  transition={{ duration: 0.5, ease: EASE }}
                  aria-hidden={i === wordIndex ? undefined : "true"}
                >
                  {word}
                </motion.span>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
